import {
  BoundaryIdSchema,
  CreatableBillingModeSchema,
  CreateInvitationRequestSchema,
  OrganizationIdSchema,
  ProjectIdSchema,
  ProjectSchema,
  RoleNameSchema,
  UpdateInvitationRequestSchema,
  UserIdSchema,
  WorkspaceIdSchema,
} from "@storytell/types";
import z from "zod";

export const ProjectIdParamsSchema = z.object({
  id: z.string(),
});

// An organization-scope invitation into an org holding no project leaves the
// focused tenancy without a workspace or a project, and the backend omits both
// rather than sending a zero id no row carries.
export const AcceptInvitationResponseSchema = z.object({
  userId: UserIdSchema,
  tenantId: OrganizationIdSchema,
  organizationId: OrganizationIdSchema,
  workspaceId: WorkspaceIdSchema.optional(),
  projectId: ProjectIdSchema.optional(),
});

export const InvitationIdParamsSchema = z.object({
  invitationId: BoundaryIdSchema,
});

// Names are cosmetic and the project pair is absent at organization scope.
// Nullish rather than optional: a Go nil serializes as null, which
// `.optional()` alone rejects.
export const InvitationDetailSchema = z.object({
  invitationId: BoundaryIdSchema,
  scope: z.enum(["organization", "project"]),
  organizationId: OrganizationIdSchema,
  organizationName: z.string().nullish(),
  projectId: ProjectIdSchema.nullish(),
  projectName: z.string().nullish(),
  role: RoleNameSchema,
  invitedByEmail: z.string().nullish(),
  expiresAt: z.string().nullish(),
  status: z.enum(["pending", "accepted", "revoked", "expired"]),
});
export type InvitationDetail = z.infer<typeof InvitationDetailSchema>;

export const UpdateInvitationInputSchema = UpdateInvitationRequestSchema;

// The backend expects just an array of invitations, not wrapped in an object
// The tenancy is obtained from the authentication context on the backend
export const CreateInvitationsInputSchema = z.array(CreateInvitationRequestSchema);

export const CreateProjectInputSchema = ProjectSchema.pick({
  name: true,
  description: true,
}).extend({
  invites: z.array(CreateInvitationRequestSchema).optional(),
  billingMode: CreatableBillingModeSchema.optional(),
});

export const UpdateProjectInputSchema = ProjectSchema.pick({
  name: true,
  description: true,
});

// Sync endpoint returns a map of project IDs to project objects
export const ProjectsSyncSchema = z.object({
  projects: z.record(ProjectIdSchema, ProjectSchema),
});
